import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// ── Sample journey data ─────────────────────────────────────────────────────
// Illustrative only -- not tied to any real user. Data-driven so any future
// app activity (a live class, a mentor check-in, a new capsule, etc.) can be
// slotted in as just another array entry.

interface Milestone {
  icon: string;
  title: string;
  desc: string;
  badgeLabel: string;
  color: string;
}

// Fixed-order categorical palette, reused from the validated set already in
// admin/RevenueDashboard.tsx (CVD-safe on light + dark surfaces) -- assigned
// by index, one hue per milestone, never re-cycled.
const MILESTONES: Milestone[] = [
  { icon: '🎉', title: 'Student Enrolled', desc: "Welcome aboard! You're ready to learn.", badgeLabel: 'New Learner', color: '#9085e9' },
  { icon: '🧭', title: 'Mentor Assigned', desc: 'A mentor is assigned to guide you.', badgeLabel: 'Guided Learner', color: '#3987e5' },
  { icon: '📚', title: 'Course Assigned', desc: 'Your learning path is ready.', badgeLabel: 'On Track', color: '#199e70' },
  { icon: '📈', title: 'Track Progress', desc: 'Keep learning and track your progress.', badgeLabel: 'Progress Tracker', color: '#22c55e' },
  { icon: '📝', title: 'Test Assigned', desc: 'Tests are assigned to evaluate you.', badgeLabel: 'Assessment Ready', color: '#c98500' },
  { icon: '🌱', title: 'Personal Development Course', desc: 'Your mentor assigns a course for your growth.', badgeLabel: 'Growth Seeker', color: '#d95926' },
  { icon: '🧩', title: 'Capsule Knowledge', desc: 'Get bite-sized knowledge to sharpen skills.', badgeLabel: 'Knowledge Explorer', color: '#d55181' },
  { icon: '🏆', title: 'Course Completion', desc: "Congratulations! You did it.", badgeLabel: 'Course Achiever', color: '#e66767' },
];

const STEP_FEATURES = [
  { icon: '🎯', title: 'Milestone Based', desc: 'Track your journey through important milestones.', bg: 'bg-indigo-50', ring: 'text-indigo-600' },
  { icon: '🏅', title: 'Earn Badges', desc: 'Stay motivated with badges and reward points.', bg: 'bg-amber-50', ring: 'text-amber-600' },
  { icon: '📊', title: 'Track Metrics', desc: 'Monitor your learning progress and performance.', bg: 'bg-blue-50', ring: 'text-blue-600' },
  { icon: '🔔', title: 'Smart Notifications', desc: 'Get updates on activities, tests and milestones.', bg: 'bg-orange-50', ring: 'text-orange-600' },
  { icon: '📄', title: 'Detailed Reports', desc: 'View insights and reports to improve continuously.', bg: 'bg-teal-50', ring: 'text-teal-600' },
];

const ACTIVITIES = [
  { icon: '📋', label: 'Assignments' },
  { icon: '📝', label: 'Quizzes' },
  { icon: '🎥', label: 'Live Classes' },
  { icon: '❓', label: 'Doubt Sessions' },
  { icon: '📁', label: 'Projects' },
  { icon: '📖', label: 'Notes & Resources' },
  { icon: '💬', label: 'Discussions' },
];

const STAT_TILES = [
  { label: 'Courses Enrolled', value: '12' },
  { label: 'Tests Taken', value: '34' },
  { label: 'Avg. Score', value: '85%' },
  { label: 'Badges Earned', value: '18' },
];

const PROGRESS_OVER_TIME = [
  { month: 'Jan', value: 20 },
  { month: 'Feb', value: 35 },
  { month: 'Mar', value: 48 },
  { month: 'Apr', value: 60 },
  { month: 'May', value: 78 },
  { month: 'Jun', value: 92 },
];

const INDIGO = '#4f46e5';
const OVERALL_PROGRESS = 72;

const ReportTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-slate-900 mb-0.5">{label}</p>
      <p className="text-slate-500">
        Progress: <span className="text-slate-900 font-medium">{payload[0].value}%</span>
      </p>
    </div>
  );
};

const ProgressRing: React.FC<{ percent: number; size?: number; label: string }> = ({ percent, size = 128, label }) => {
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={INDIGO}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-2xl font-extrabold text-slate-900">{percent}%</span>
        <span className="text-[11px] text-slate-500 text-center px-2">{label}</span>
      </div>
    </div>
  );
};

const MilestoneNode: React.FC<{ m: Milestone; index: number }> = ({ m, index }) => (
  <div className="flex flex-col items-center text-center">
    <div
      className="relative z-10 w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center text-2xl lg:text-3xl shadow-md"
      style={{ backgroundColor: m.color }}
    >
      {m.icon}
    </div>
    <span
      className="text-[11px] font-bold text-white rounded-full w-6 h-6 flex items-center justify-center -mt-3 mb-2 z-20 border-2 border-white shadow-sm"
      style={{ backgroundColor: '#0f172a' }}
    >
      {String(index + 1).padStart(2, '0')}
    </span>
    <h3 className="text-sm font-bold text-slate-900 mb-1 leading-tight">{m.title}</h3>
    <p className="text-xs text-slate-500 leading-relaxed mb-3 max-w-[9.5rem]">{m.desc}</p>
    <div className="w-px h-3 bg-slate-300" />
    <div
      className="inline-flex flex-col items-center gap-0.5 border rounded-xl px-3 py-1.5"
      style={{ backgroundColor: `${m.color}14`, borderColor: `${m.color}40` }}
    >
      <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">Badge Earned</span>
      <span className="text-xs font-bold flex items-center gap-1" style={{ color: m.color }}>
        ⭐ {m.badgeLabel}
      </span>
    </div>
  </div>
);

export const RoadmapSection: React.FC = () => {
  return (
    <section id="student-roadmap" className="py-20 px-5 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
            Your Journey. Your Growth. Our Guidance.
          </div>
        </div>
        <h2 className="text-center text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
          Roadmap to <span className="text-indigo-600">Success</span>
        </h2>
        <p className="text-center text-slate-500 text-base max-w-2xl mx-auto mb-14">
          A personalized learning journey with milestones, achievements and continuous support.
        </p>

        {/* ── 8-step roadmap: horizontal on desktop ─────────────── */}
        <div className="hidden lg:block relative mb-16">
          <div
            className="absolute top-8 left-[6.25%] right-[6.25%] h-1 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${MILESTONES.map((m) => m.color).join(', ')})`,
            }}
          />
          <div className="grid grid-cols-8 gap-3 relative">
            {MILESTONES.map((m, i) => (
              <MilestoneNode key={m.title} m={m} index={i} />
            ))}
          </div>
        </div>

        {/* ── 8-step roadmap: vertical timeline on mobile/tablet ─── */}
        <div className="lg:hidden relative mb-16 max-w-md mx-auto">
          <div className="absolute left-8 top-2 bottom-2 w-px bg-slate-200" />
          <div className="space-y-6">
            {MILESTONES.map((m, i) => (
              <div key={m.title} className="relative flex items-start gap-5">
                <div
                  className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl shadow-md"
                  style={{ backgroundColor: m.color }}
                >
                  {m.icon}
                </div>
                <div className="flex-1 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className="text-[10px] font-bold text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#0f172a' }}
                    >
                      {i + 1}
                    </span>
                    <h3 className="text-sm font-bold text-slate-900">{m.title}</h3>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mb-2">{m.desc}</p>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full border"
                    style={{ backgroundColor: `${m.color}14`, borderColor: `${m.color}40`, color: m.color }}
                  >
                    ⭐ Badge Earned: {m.badgeLabel}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Two-column feature/analytics panel ──────────────────── */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: features + activities */}
          <div className="lg:col-span-3 bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2">
              <span className="text-indigo-500">★</span> Every Step Matters – Every Achievement Counts
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {STEP_FEATURES.map((f) => (
                <div key={f.title} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className={`w-9 h-9 rounded-lg ${f.bg} flex items-center justify-center text-lg mb-2`}>{f.icon}</div>
                  <h4 className="text-xs font-bold text-slate-900 mb-1">{f.title}</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-bold text-slate-900 mb-4">Activities That Can Be Added at Any Milestone</h3>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 mb-4">
              {ACTIVITIES.map((a) => (
                <div key={a.label} className="flex flex-col items-center text-center gap-1.5">
                  <div className="w-11 h-11 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-lg">
                    {a.icon}
                  </div>
                  <span className="text-[10px] text-slate-600 font-medium leading-tight">{a.label}</span>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 bg-white border border-dashed border-slate-300 rounded-xl px-4 py-3">
              <span className="text-indigo-500 text-sm">★</span>
              <p className="text-xs text-slate-500">Any activity in the app can be linked to any milestone in your roadmap.</p>
            </div>
          </div>

          {/* Right: Track. Analyze. Improve. */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col">
            <h3 className="text-base font-bold text-slate-900 mb-5">Track. Analyze. Improve.</h3>

            <div className="flex items-center gap-4 mb-6">
              <ProgressRing percent={OVERALL_PROGRESS} label="Course Progress" />
              <div className="grid grid-cols-2 gap-2 flex-1">
                {STAT_TILES.map((s) => (
                  <div key={s.label} className="bg-slate-50 rounded-lg p-2.5">
                    <p className="text-lg font-extrabold text-slate-900 leading-none">{s.value}</p>
                    <p className="text-[10px] text-slate-500 mt-1 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs font-semibold text-slate-700 mb-2">Progress Over Time</p>
            <div style={{ width: '100%', height: 140 }} className="mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={PROGRESS_OVER_TIME} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                    width={38}
                  />
                  <Tooltip content={<ReportTooltip />} cursor={{ stroke: INDIGO, strokeWidth: 1, strokeDasharray: '3 3' }} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Progress"
                    stroke={INDIGO}
                    strokeWidth={2}
                    dot={{ r: 4, fill: INDIGO, strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <p className="text-xs font-semibold text-slate-700 mb-2">Badge Collection</p>
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {['#c98500', '#3987e5', '#199e70', '#9085e9'].map((c, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-base shadow-sm"
                    style={{ backgroundColor: `${c}22`, border: `1px solid ${c}55` }}
                  >
                    <span style={{ color: c }}>⭐</span>
                  </div>
                ))}
              </div>
              <a href="/auth/register" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 whitespace-nowrap">
                View All Badges →
              </a>
            </div>

            <div className="mt-5 flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
              <span className="text-indigo-500 text-sm">★</span>
              <p className="text-xs text-indigo-700">
                Detailed reports help you and your mentor make better decisions for your learning journey.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapSection;
